import { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import type { Property } from "../../../store/api/propertiesApi";

interface PropertyTableProps {
	properties: Property[];
	selectedProperties: string[];
	onPropertySelect: (propertyId: string) => void;
	onSelectAll: () => void;
	onPropertyClick: (property: Property) => void;
	onEditProperty: (property: Property) => void;
	onDeleteProperty: (propertyId: string) => void;
}

const PropertyTable: React.FC<PropertyTableProps> = ({
	properties,
	selectedProperties,
	onPropertySelect,
	onSelectAll,
	onPropertyClick,
	onEditProperty,
	onDeleteProperty,
}) => {
	const [sortField, setSortField] = useState<string>("name");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const sortedProperties = [...properties].sort((a, b) => {
		let aValue: any = a[sortField as keyof Property];
		let bValue: any = b[sortField as keyof Property];

		if (typeof aValue === "string") {
			aValue = aValue.toLowerCase();
			bValue = bValue.toLowerCase();
		}

		if (sortDirection === "asc") {
			return aValue > bValue ? 1 : -1;
		} else {
			return aValue < bValue ? 1 : -1;
		}
	});

	const getStatusColor = (status: string) => {
		switch (status) {
			case "available":
				return "bg-success/10 text-success";
			case "pending":
				return "bg-blue-500/10 text-blue-600";
			case "leased":
				return "bg-purple-500/10 text-purple-600";
			case "sold":
				return "bg-error/10 text-error";
			case "maintenance":
				return "bg-warning/10 text-warning";
			default:
				return "bg-muted text-muted-foreground";
		}
	};

	const getStatusLabel = (status: string) => {
		const labels: Record<string, string> = {
			available: "Available",
			pending: "Pending Deal",
			leased: "Leased",
			sold: "Sold",
			maintenance: "Maintenance",
		};
		return labels[status] || status;
	};

	const formatCurrency = (amount?: number) => {
		if (!amount) return "N/A";
		return new Intl.NumberFormat("en-NG", {
			style: "currency",
			currency: "NGN",
		}).format(amount);
	};

	return (
		<div className="bg-card rounded-lg border border-border overflow-hidden">
			{/* Table with horizontal scroll on mobile */}
			<div className="overflow-x-auto -mx-6 sm:mx-0">
				<div className="inline-block min-w-full align-middle sm:px-0">
					<table className="min-w-full divide-y divide-border">
						<thead className="bg-muted/50 border-b border-border">
							<tr>
								<th className="w-12 p-4">
									<input
										type="checkbox"
										checked={
											properties.length > 0 &&
											selectedProperties.length === properties.length
										}
										onChange={onSelectAll}
										className="rounded border-border"
									/>
								</th>
								<th className="text-left p-4 font-medium text-xs text-foreground">
									<button
										onClick={() => handleSort("name")}
										className="flex items-center space-x-1 hover:text-primary"
									>
										<span>Property</span>
										<Icon
											name={
												sortField === "name" && sortDirection === "desc"
													? "ChevronDown"
													: "ChevronUp"
											}
											size={16}
										/>
									</button>
								</th>
								<th className="text-left p-4 font-medium text-xs text-foreground">
									<button
										onClick={() => handleSort("property_type")}
										className="flex items-center space-x-1 hover:text-primary"
									>
										<span>Type</span>
										<Icon
											name={
												sortField === "property_type" &&
												sortDirection === "desc"
													? "ChevronDown"
													: "ChevronUp"
											}
											size={16}
										/>
									</button>
								</th>
								<th className="text-left p-4 font-medium text-xs text-foreground">
									<button
										onClick={() => handleSort("rent_amount")}
										className="flex items-center space-x-1 hover:text-primary"
									>
										<span>Pricing</span>
										<Icon
											name={
												sortField === "rent_amount" && sortDirection === "desc"
													? "ChevronDown"
													: "ChevronUp"
											}
											size={16}
										/>
									</button>
								</th>
								<th className="text-left p-4 font-medium text-xs text-foreground">
									Status
								</th>
								<th className="text-right p-4 font-medium text-xs text-foreground">
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{sortedProperties.map((property) => (
								<tr
									key={property.id}
									className="border-b border-border hover:bg-muted/30 cursor-pointer smooth-transition"
									onClick={() => onPropertyClick(property)}
								>
									<td className="p-4" onClick={(e) => e.stopPropagation()}>
										<input
											type="checkbox"
											checked={selectedProperties.includes(property.id)}
											onChange={() => onPropertySelect(property.id)}
											className="rounded border-border"
										/>
									</td>
									<td className="p-4">
										<div className="flex items-center space-x-3">
											<div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
												{property.images && property.images.length > 0 ? (
													<img
														src={property.images[0]}
														alt={property.name}
														className="w-full h-full object-cover"
													/>
												) : (
													<Icon
														name="Building2"
														size={24}
														className="text-muted-foreground"
													/>
												)}
											</div>
											<div>
												<p className="font-medium text-foreground">
													{property.name}
												</p>
												{property.unit_number && (
													<p className="text-xs text-muted-foreground font-medium">
														Unit: {property.unit_number}
													</p>
												)}
												<p className="text-sm text-muted-foreground">
													{property.address}
												</p>
												{(property.city || property.state) && (
													<p className="text-xs text-muted-foreground">
														{property.city}
														{property.city && property.state ? ", " : ""}
														{property.state}
													</p>
												)}
											</div>
										</div>
									</td>
									<td className="p-4">
										<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
											{property.property_type}
										</span>
									</td>
									<td className="p-4">
										{/* Check for new rent_amount field or legacy monthly_rent */}
										{((property.rent_amount && property.rent_amount > 0) ||
											(property.monthly_rent && property.monthly_rent > 0)) &&
										property.purchase_price &&
										property.purchase_price > 0 ? (
											// Both lease and sale pricing
											<div className="space-y-1">
												<div className="flex items-center gap-1">
													<Icon name="Key" size={12} className="text-blue-600" />
													<p className="text-xs font-medium text-foreground">
														{formatCurrency(
															property.rent_amount || property.monthly_rent,
														)}
													</p>
												</div>
												<div className="flex items-center gap-1">
													<Icon name="Tag" size={12} className="text-green-600" />
													<p className="text-xs font-medium text-foreground">
														{formatCurrency(property.purchase_price)}
													</p>
												</div>
											</div>
										) : (property.rent_amount && property.rent_amount > 0) ||
										  (property.monthly_rent && property.monthly_rent > 0) ? (
											// Lease only
											<>
												<p className="font-medium text-foreground">
													{formatCurrency(
														property.rent_amount || property.monthly_rent,
													)}
												</p>
												<p className="text-sm text-muted-foreground">
													{property.rent_type
														? `per ${property.rent_type === "annual" ? "year" : property.rent_type === "biannual" ? "6 months" : property.rent_type === "monthly" ? "month" : "week"}`
														: "per month"}
												</p>
											</>
										) : property.purchase_price && property.purchase_price > 0 ? (
											// Sale only
											<>
												<p className="font-medium text-foreground">
													{formatCurrency(property.purchase_price)}
												</p>
												<p className="text-sm text-muted-foreground">for sale</p>
											</>
										) : (
											// No pricing
											<p className="text-muted-foreground">N/A</p>
										)}
									</td>
									<td className="p-4">
										<span
											className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
												property.status,
											)}`}
										>
											{getStatusLabel(property.status)}
										</span>
									</td>
									<td className="p-4" onClick={(e) => e.stopPropagation()}>
										<div className="flex items-center justify-end space-x-1">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onEditProperty(property)}
												className="h-8 w-8"
											>
												<Icon name="Edit" size={16} />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												onClick={() => onDeleteProperty(property.id)}
												className="h-8 w-8 text-destructive hover:text-destructive"
											>
												<Icon name="Trash2" size={16} />
											</Button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{/* Mobile Card Layout - Optional, can be removed if table scrolling is preferred */}
			<div className="lg:hidden">
				{sortedProperties.map((property) => (
					<div
						key={property.id}
						className="p-4 border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer smooth-transition"
						onClick={() => onPropertyClick(property)}
					>
						<div className="flex items-start space-x-3">
							<input
								type="checkbox"
								checked={selectedProperties.includes(property.id)}
								onChange={() => onPropertySelect(property.id)}
								onClick={(e) => e.stopPropagation()}
								className="mt-1 rounded border-border"
							/>
							<div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
								{property.images && property.images.length > 0 ? (
									<img
										src={property.images[0]}
										alt={property.name}
										className="w-full h-full object-cover"
									/>
								) : (
									<Icon
										name="Building2"
										size={24}
										className="text-muted-foreground"
									/>
								)}
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-start justify-between">
									<div className="flex-1 min-w-0">
										<p className="font-medium text-foreground truncate">
											{property.name}
										</p>
										<p className="text-sm text-muted-foreground truncate">
											{property.address}
										</p>
										{(property.city || property.state) && (
											<p className="text-xs text-muted-foreground">
												{property.city}
												{property.city && property.state ? ", " : ""}
												{property.state}
											</p>
										)}
									</div>
									<div className="flex items-center space-x-1 ml-2">
										<Button
											variant="ghost"
											size="icon"
											onClick={(e) => {
												e.stopPropagation();
												onEditProperty(property);
											}}
											className="h-8 w-8"
										>
											<Icon name="Edit" size={16} />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											onClick={(e) => {
												e.stopPropagation();
												onDeleteProperty(property.id);
											}}
											className="h-8 w-8 text-destructive hover:text-destructive"
										>
											<Icon name="Trash2" size={16} />
										</Button>
									</div>
								</div>
								<div className="mt-2 flex items-center space-x-4 text-sm">
									<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
										{property.property_type}
									</span>
									<span
										className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(
											property.status,
										)}`}
									>
										{property.status}
									</span>
								</div>
								<div className="mt-2 flex items-center space-x-4 text-sm">
									{property.bedrooms !== undefined && (
										<span className="text-muted-foreground">
											{property.bedrooms} bed
											{property.bedrooms !== 1 ? "s" : ""}
										</span>
									)}
									{property.bathrooms !== undefined && (
										<span className="text-muted-foreground">
											{property.bathrooms} bath
											{property.bathrooms !== 1 ? "s" : ""}
										</span>
									)}
									{property.monthly_rent && (
										<span className="font-medium text-foreground">
											{formatCurrency(property.monthly_rent)}/mo
										</span>
									)}
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default PropertyTable;
